/** AppShell (AppShell.dc.html) — sidebar desktop + drawer/bottom-nav mobil, card SOLD CLIPURI. */
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useBalance, useNotifications, useSupportThreads } from '../api/hooks';
import { useTheme } from '../lib/theme';
import { counterLabel } from '../lib/format';
import { Logo } from './Logo';

interface NavItem {
  label: string;
  to: string;
  badge?: number;
}
interface NavGroup {
  title: string;
  items: NavItem[];
}

function useNav(): { groups: NavGroup[]; flat: NavItem[] } {
  const support = useSupportThreads();
  const notif = useNotifications();
  const supportUnread = support.data?.unread ?? 0;
  const notifUnread = notif.data?.unread ?? 0;
  return useMemo(() => {
    const groups: NavGroup[] = [
      {
        title: 'Studio',
        items: [
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Creare clip', to: '/creeaza' },
          { label: 'Campanii', to: '/campanii' },
          { label: 'Livrări', to: '/livrari' },
        ],
      },
      {
        title: 'Bibliotecă',
        items: [
          { label: 'Galerie fișiere', to: '/galerie' },
          { label: 'Produse', to: '/produse' },
        ],
      },
      {
        title: 'Cont',
        items: [
          { label: 'Comenzi Ultra', to: '/ultra' },
          { label: 'Cumpără clipuri', to: '/cumpara' },
          { label: 'Notificări', to: '/notificari', badge: notifUnread || undefined },
          { label: 'Suport', to: '/suport', badge: supportUnread || undefined },
          { label: 'Setări', to: '/setari' },
        ],
      },
    ];
    const flat: NavItem[] = [];
    groups.forEach((g) => g.items.forEach((it) => flat.push(it)));
    return { groups, flat };
  }, [supportUnread, notifUnread]);
}

function SoldCard({ compact }: { compact?: boolean }) {
  const { data } = useBalance();
  const counters = data?.counters;
  const rows = (['organic_premium', 'organic_ultra', 'reclame_premium', 'reclame_ultra'] as const).map((c) => ({
    key: c,
    label: counterLabel(c),
    n: counters?.[c] ?? 0,
    dot: c.startsWith('reclame') ? 'var(--accent)' : 'var(--ok)',
  }));
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--line)', borderRadius: 13, padding: '13px 13px 11px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Sold clipuri
        </span>
        <Link to="/cumpara" style={{ fontSize: 11.5, fontWeight: 800, textDecoration: 'none' }}>
          + Adaugă
        </Link>
      </div>
      <div style={{ display: compact ? 'grid' : 'flex', gridTemplateColumns: '1fr 1fr', gap: compact ? 8 : 7, flexDirection: 'column' }}>
        {rows.map((r) => (
          <div
            key={r.key}
            data-testid={`sold-${r.key}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: compact ? 'var(--surface)' : 'transparent', borderRadius: 10, padding: compact ? '9px 11px' : 0 }}
          >
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: r.dot }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>{r.label}</span>
            <span
              data-testid={`sold-val-${r.key}`}
              className="tnum"
              style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 800, color: r.n === 0 ? 'var(--warn)' : 'var(--ink)' }}
            >
              {r.n}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThemeToggle({ big }: { big?: boolean }) {
  const { resolved, toggle } = useTheme();
  const isDark = resolved === 'dark';
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label="Comută tema"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: big ? '14px 16px' : '10px 12px',
        width: '100%',
        border: '1px solid var(--line)',
        borderRadius: big ? 13 : 11,
        background: 'var(--surface)',
        fontWeight: 700,
        fontSize: big ? 14 : 13,
        color: 'var(--muted)',
      }}
    >
      <span>{isDark ? 'Temă luminoasă' : 'Temă întunecată'}</span>
      <span aria-hidden="true" style={{ position: 'relative', width: big ? 48 : 42, height: big ? 27 : 24, borderRadius: 999, border: '1px solid var(--line)', background: 'var(--bg2)', flex: 'none' }}>
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: 2,
            width: big ? 21 : 18,
            height: big ? 21 : 18,
            borderRadius: '50%',
            background: 'var(--ink)',
            transform: `translateX(${isDark ? (big ? 21 : 18) : 0}px)`,
            transition: 'transform .28s cubic-bezier(.3,.7,.3,1.1)',
          }}
        />
      </span>
    </button>
  );
}

function navLinkStyle(isActive: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 10,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: isActive ? 800 : 700,
    background: isActive ? 'var(--ink)' : 'transparent',
    color: isActive ? 'var(--bg)' : 'var(--ink)',
  } as const;
}

export function AppShell() {
  const { groups, flat } = useNav();
  const { data: balance } = useBalance();
  const [menu, setMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menu ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menu]);

  const totalSold = balance ? Object.values(balance.counters).reduce((a, b) => a + b, 0) : 0;

  return (
    <div>
      {/* ===== Sidebar desktop ===== */}
      <aside
        className="rv-sidebar"
        aria-label="Navigare aplicație"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: 248,
          zIndex: 90,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          borderRight: '1px solid var(--line)',
          padding: '18px 14px 16px',
          boxSizing: 'border-box',
          overflow: 'auto',
        }}
      >
        <Link to="/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, color: 'var(--ink)', textDecoration: 'none', padding: '2px 4px' }}>
          <Logo withWordmark />
        </Link>
        <Link to="/creeaza" className="btn btn-primary" style={{ marginTop: 16, padding: 13, fontSize: 15 }}>
          <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1 }}>
            +
          </span>
          Creează clip
        </Link>
        <nav style={{ display: 'flex', flexDirection: 'column', marginTop: 6 }}>
          {groups.map((g) => (
            <div key={g.title}>
              <p style={{ margin: '16px 6px 6px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                {g.title}
              </p>
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} style={({ isActive }) => navLinkStyle(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className="mono" style={{ fontSize: 10.5, fontWeight: 700, color: isActive ? 'var(--accent)' : 'var(--accent-text)', width: 16 }}>
                        {String(flat.indexOf(it) + 1).padStart(2, '0')}
                      </span>
                      {it.label}
                      {it.badge ? (
                        <span className="tnum" style={{ marginLeft: 'auto', background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '2px 7px' }}>
                          {it.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 18 }}>
          <SoldCard />
          <ThemeToggle />
          <Link to="/setari" title="Cont & setări" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'var(--ink)', padding: '6px 4px 2px' }}>
            <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12, flex: 'none' }}>
              RV
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Contul meu</span>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Cont &amp; setări</span>
            </span>
          </Link>
        </div>
      </aside>

      {/* ===== Header mobil ===== */}
      <header
        className="rv-mobile-header"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 90,
          height: 60,
          background: 'var(--hdr)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--line)',
          display: 'none',
          alignItems: 'center',
          gap: 10,
          padding: '0 14px',
        }}
      >
        <Link to="/dashboard" aria-label="Dashboard" style={{ display: 'inline-flex' }}>
          <Logo />
        </Link>
        <button
          onClick={() => setMenu(true)}
          className="tnum"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '8px 13px', fontSize: 12.5, fontWeight: 800 }}
        >
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)' }} />
          {totalSold} clipuri în sold
        </button>
      </header>

      {/* ===== Bottom nav mobil ===== */}
      <nav
        className="rv-bottom-nav"
        aria-label="Navigare rapidă"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 90,
          background: 'var(--hdr)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderTop: '1px solid var(--line)',
          display: 'none',
          gridTemplateColumns: '1fr 1fr auto 1fr 1fr',
          alignItems: 'center',
          padding: '6px 8px calc(6px + env(safe-area-inset-bottom))',
          gap: 2,
        }}
      >
        <BottomLink to="/dashboard" label="Acasă" />
        <BottomLink to="/campanii" label="Campanii" />
        <Link
          to="/creeaza"
          aria-label="Creează clip"
          style={{ width: 54, height: 54, margin: '-18px 10px 0', borderRadius: '50%', background: 'var(--accent)', color: 'var(--accent-ink)', display: 'grid', placeItems: 'center', textDecoration: 'none', fontSize: 26, fontWeight: 800, boxShadow: '0 10px 24px -8px rgba(232,78,54,.7)', lineHeight: 1 }}
        >
          +
        </Link>
        <BottomLink to="/livrari" label="Livrări" />
        <button onClick={() => setMenu(true)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '7px 2px', borderRadius: 10, color: 'var(--muted)' }}>
          <span aria-hidden="true" style={{ width: 14, height: 2, borderRadius: 2, background: 'currentColor', boxShadow: '0 5px 0 currentColor,0 -5px 0 currentColor' }} />
          <span style={{ fontSize: 10.5, fontWeight: 800, marginTop: 3 }}>Meniu</span>
        </button>
      </nav>

      {/* ===== Drawer mobil ===== */}
      {menu && (
        <div
          className="rv-fade"
          style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'var(--hdr)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)', overflow: 'auto', padding: '14px 18px calc(90px + env(safe-area-inset-bottom))', boxSizing: 'border-box' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="bricolage" style={{ fontWeight: 800, fontSize: 19 }}>
              Meniu
            </span>
            <button onClick={() => setMenu(false)} aria-label="Închide" style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid var(--line)', background: 'var(--surface)', fontSize: 17, fontWeight: 800 }}>
              ✕
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <SoldCard compact />
          </div>
          <nav style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
            {flat.map((it, i) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setMenu(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 4px', borderBottom: '1px solid var(--line)', textDecoration: 'none' }}
              >
                <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent-text)' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="bricolage" style={{ fontSize: 21, fontWeight: 700, color: 'var(--muted)' }}>
                  {it.label}
                </span>
                {it.badge ? (
                  <span style={{ background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '3px 8px' }}>{it.badge}</span>
                ) : null}
                <span aria-hidden="true" style={{ marginLeft: 'auto', color: 'var(--muted)' }}>
                  →
                </span>
              </Link>
            ))}
          </nav>
          <div style={{ marginTop: 14 }}>
            <ThemeToggle big />
          </div>
        </div>
      )}

      {/* ===== Conținut ===== */}
      <main className="rv-main" style={{ marginLeft: 248, minHeight: '100vh', padding: '28px 30px 60px', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function BottomLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '7px 2px', textDecoration: 'none', borderRadius: 10, color: isActive ? 'var(--ink)' : 'var(--muted)' })}>
      {({ isActive }) => (
        <>
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? 'var(--accent)' : 'var(--line)' }} />
          <span style={{ fontSize: 10.5, fontWeight: 800 }}>{label}</span>
        </>
      )}
    </NavLink>
  );
}
